import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@mailflow/ui';
import type { ImportResult } from '@mailflow/shared';
import {
  uploadImportFile,
  importFromText,
  importFromUrl,
  useRunImport,
  useCreateList,
  listsQuery,
  type UploadResponse,
} from '../lib/contacts.js';

const FIELD_LABELS: Record<string, string> = {
  email: 'E-Mail-Adresse',
  firstName: 'Vorname',
  lastName: 'Nachname',
  phone: 'Telefon',
  company: 'Firma (Name)',
  street: 'Firma: Straße / Adresse',
  postalCode: 'Firma: PLZ',
  country: 'Land',
  city: 'Ort',
  website: 'Firma: Website',
  googleMapsUrl: 'Firma: Google Maps URL',
  googlePlaceId: 'Firma: Google Place ID',
  rating: 'Firma: Sterne-Bewertung',
  category: 'Firma: Kategorie',
  source: 'Quelle',
  tag: 'Tag / Schlagwort',
  ignore: 'Nicht importieren',
};

type Step = 'upload' | 'mapping' | 'done';
type Source = 'datei' | 'einfuegen' | 'adresse';

export function ImportPage() {
  const [step, setStep] = useState<Step>('upload');
  const [source, setSource] = useState<Source>('datei');
  const [pasted, setPasted] = useState('');
  const [url, setUrl] = useState('');
  const [upload, setUpload] = useState<UploadResponse | null>(null);
  const [mapping, setMapping] = useState<Array<{ column: string; field: string }>>([]);
  const [listId, setListId] = useState('');
  const [listFormOpen, setListFormOpen] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [updateExisting, setUpdateExisting] = useState(true);
  const [consentStatus, setConsentStatus] = useState('unknown');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  const lists = useQuery(listsQuery);
  const runImport = useRunImport();
  const createList = useCreateList();

  /** Die frisch angelegte Liste wird gleich ausgewählt — sonst waere der Weg nur halb. */
  async function handleCreateList() {
    const name = newListName.trim();
    if (name.length === 0) return;

    const { list } = await createList.mutateAsync({ name });
    setListId(list.id);
    setNewListName('');
    setListFormOpen(false);
  }

  const pastedLines = pasted.split(/\r?\n/).filter((line) => line.trim().length > 0).length;

  /** Ein Weg für alle drei Quellen — was ankommt, ist danach identisch. */
  async function accept(load: () => Promise<UploadResponse>, fallbackMessage: string) {
    setUploadError(null);
    setBusy(true);
    try {
      const response = await load();
      setUpload(response);
      setMapping(response.preview.mapping.map((m) => ({ column: m.column, field: m.field })));
      setStep('mapping');
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : fallbackMessage);
    } finally {
      setBusy(false);
    }
  }

  async function handleFile(file: File) {
    await accept(() => uploadImportFile(file), 'Upload fehlgeschlagen.');
  }

  async function handlePaste() {
    await accept(
      () => importFromText(pasted),
      'Die eingefügten Daten konnten nicht gelesen werden.',
    );
  }

  async function handleUrl() {
    await accept(() => importFromUrl(url.trim()), 'Die Adresse konnte nicht geladen werden.');
  }

  async function handleRun() {
    if (!upload) return;
    const outcome = await runImport.mutateAsync({
      uploadId: upload.uploadId,
      mapping,
      listId: listId || null,
      updateExisting,
      consentStatus,
    });
    setResult(outcome);
    setStep('done');
  }

  const emailColumns = mapping.filter((m) => m.field === 'email').length;
  const mappingProblem =
    emailColumns === 0
      ? 'Genau eine Spalte muss der E-Mail-Adresse zugeordnet sein.'
      : emailColumns > 1
        ? 'Mehrere Spalten sind der E-Mail-Adresse zugeordnet — es darf nur eine sein.'
        : null;

  return (
    <div className="stack" style={{ gap: 'var(--space-6)', maxWidth: '900px' }}>
      <nav className="breadcrumb text-sm">
        <Link to="/kontakte">Kontakte</Link>
        <span aria-hidden="true"> / </span>
        <span className="muted">Import</span>
      </nav>

      <header>
        <h1 className="text-2xl">Kontakte importieren</h1>
        <p className="muted text-sm">
          Drei Schritte: Quelle wählen, Spalten bestätigen, übernehmen. Vor dem letzten Schritt wird
          nichts geschrieben. Als Quelle geht eine Datei (CSV oder Excel), eine eingefügte Tabelle
          aus der Zwischenablage oder eine Adresse im Netz.
        </p>
      </header>

      <ol className="steps" aria-label="Fortschritt">
        <StepMarker index={1} label="Quelle" active={step === 'upload'} done={step !== 'upload'} />
        <StepMarker
          index={2}
          label="Zuordnung"
          active={step === 'mapping'}
          done={step === 'done'}
        />
        <StepMarker index={3} label="Ergebnis" active={step === 'done'} done={false} />
      </ol>

      {step === 'upload' && (
        <section className="card">
          <div className="card__body stack">
            <div role="tablist" aria-label="Quelle wählen" className="tabs__list">
              <SourceTab
                id="datei"
                label="Datei"
                hint="CSV oder Excel"
                active={source === 'datei'}
                onSelect={setSource}
              />
              <SourceTab
                id="einfuegen"
                label="Einfügen"
                hint="aus der Zwischenablage"
                active={source === 'einfuegen'}
                onSelect={setSource}
              />
              <SourceTab
                id="adresse"
                label="Adresse"
                hint="Datei im Netz"
                active={source === 'adresse'}
                onSelect={setSource}
              />
            </div>

            {uploadError && (
              <div className="alert" data-tone="danger" role="alert">
                {uploadError}
              </div>
            )}

            {source === 'datei' && (
              <div className="stack" role="tabpanel" aria-label="Datei">
                <h2 className="text-lg">Datei wählen</h2>
                <p className="muted text-sm">
                  CSV oder Excel (.xlsx). Komma, Semikolon oder Tabulator als Trennzeichen — das
                  erkennt das System selbst, Exporte mit BOM ebenso. Bei einer Arbeitsmappe wird das
                  erste Blatt mit Kopfzeile und Daten gelesen.
                </p>
                <input
                  className="input"
                  type="file"
                  accept=".csv,.tsv,.txt,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  disabled={busy}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleFile(file);
                  }}
                  aria-label="Datei auswählen"
                />
                <p className="field__hint">
                  Das alte Format .xls kann nicht gelesen werden — in Excel einmal als .xlsx
                  speichern.
                </p>
              </div>
            )}

            {source === 'einfuegen' && (
              <div className="stack" role="tabpanel" aria-label="Einfügen">
                <h2 style={{ fontSize: 'var(--text-lg)' }}>Tabelle einfügen</h2>
                <p className="muted text-sm">
                  In der Tabelle alles markieren (samt Kopfzeile), kopieren und hier einfügen.
                  Funktioniert mit Excel, Proton, Google Sheets und LibreOffice gleichermassen — und
                  ist der Weg für Freigabelinks, die sich nicht herunterladen lassen.
                </p>
                <textarea
                  className="input"
                  rows={10}
                  value={pasted}
                  disabled={busy}
                  spellCheck={false}
                  placeholder={'E-Mail\tVorname\tNachname\nanna@example.com\tAnna\tMuster'}
                  onChange={(e) => setPasted(e.target.value)}
                  aria-label="Tabelle einfügen"
                  style={{ fontFamily: 'var(--font-mono, monospace)', resize: 'vertical' }}
                />
                <div className="dialog__actions">
                  <Button
                    onClick={() => void handlePaste()}
                    disabled={pastedLines < 2}
                    loading={busy}
                  >
                    {pastedLines >= 2
                      ? `${(pastedLines - 1).toLocaleString('de-CH')} Zeilen übernehmen`
                      : 'Übernehmen'}
                  </Button>
                </div>
                {pasted.trim().length > 0 && pastedLines < 2 && (
                  <p className="field__hint">
                    Es braucht mindestens zwei Zeilen: die Kopfzeile und eine Datenzeile.
                  </p>
                )}
              </div>
            )}

            {source === 'adresse' && (
              <div className="stack" role="tabpanel" aria-label="Adresse">
                <h2 style={{ fontSize: 'var(--text-lg)' }}>Von einer Adresse laden</h2>
                <p className="muted text-sm">
                  Direkter Download-Link auf eine CSV- oder .xlsx-Datei, per https. Der Server holt
                  sie selbst.
                </p>
                <input
                  className="input"
                  type="url"
                  value={url}
                  disabled={busy}
                  placeholder="https://example.com/kontakte.xlsx"
                  onChange={(e) => setUrl(e.target.value)}
                  aria-label="Adresse der Datei"
                />
                <p className="field__hint">
                  Freigabelinks von Proton Drive, Google Drive oder OneDrive funktionieren hier
                  nicht: sie liefern die Anwendung, nicht die Datei — bei Proton liegt der Schlüssel
                  zudem hinter dem <code>#</code> und wird nie an einen Server gesendet. Dafür ist
                  „Einfügen" da.
                </p>
                <div className="dialog__actions">
                  <Button
                    onClick={() => void handleUrl()}
                    disabled={url.trim().length === 0}
                    loading={busy}
                  >
                    Laden
                  </Button>
                </div>
              </div>
            )}

            {busy && <p className="muted text-sm">Daten werden gelesen …</p>}
          </div>
        </section>
      )}

      {step === 'mapping' && upload && (
        <>
          <section className="card">
            <div className="card__body stack">
              <div className="page-header" style={{ alignItems: 'baseline' }}>
                <h2 style={{ fontSize: 'var(--text-lg)' }}>Spalten zuordnen</h2>
                <span className="muted text-sm" data-numeric>
                  {upload.preview.totalRowsEstimate.toLocaleString('de-CH')} Zeilen ·{' '}
                  {upload.source === 'xlsx'
                    ? `Excel-Blatt „${upload.sheetName ?? '—'}"`
                    : `Trennzeichen „${upload.preview.delimiter === '\t' ? 'Tab' : upload.preview.delimiter}"`}
                </span>
              </div>

              {mappingProblem && (
                <div className="alert" data-tone="warning" role="alert">
                  {mappingProblem}
                </div>
              )}

              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">Spalte in der Datei</th>
                      <th scope="col">Beispielwert</th>
                      <th scope="col">Zielfeld</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upload.preview.headers.map((header, index) => {
                      const suggestion = upload.preview.mapping[index];
                      return (
                        <tr key={header + String(index)}>
                          <td>
                            {header}
                            {suggestion?.inferred && (
                              <em className="nav__badge" style={{ marginLeft: 'var(--space-2)' }}>
                                erraten
                              </em>
                            )}
                          </td>
                          <td className="muted text-sm">
                            {upload.preview.sampleRows[0]?.[index] ?? '—'}
                          </td>
                          <td>
                            <select
                              className="input"
                              value={mapping[index]?.field ?? 'ignore'}
                              aria-label={`Zielfeld für Spalte ${header}`}
                              onChange={(e) =>
                                setMapping((current) =>
                                  current.map((entry, i) =>
                                    i === index ? { ...entry, field: e.target.value } : entry,
                                  ),
                                )
                              }
                            >
                              {Object.entries(FIELD_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>
                                  {label}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section className="card">
            <div className="card__body stack">
              <h2 style={{ fontSize: 'var(--text-lg)' }}>Optionen</h2>

              <div className="field">
                <label className="field__label" htmlFor="import-list">
                  Alle Kontakte zu dieser Liste hinzufügen
                </label>
                <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'stretch' }}>
                  <select
                    id="import-list"
                    className="input"
                    value={listId}
                    onChange={(e) => setListId(e.target.value)}
                    style={{ flex: 1 }}
                  >
                    <option value="">Keine Liste</option>
                    {lists.data?.items.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                  {!listFormOpen && (
                    <Button variant="secondary" onClick={() => setListFormOpen(true)}>
                      Neue Liste
                    </Button>
                  )}
                </div>

                {/*
                 * Anlegen direkt hier, nicht auf einer anderen Seite: wer
                 * gerade 800 Zeilen importiert, soll dafuer nicht den Ablauf
                 * verlassen und von vorn anfangen muessen.
                 */}
                {listFormOpen && (
                  <div
                    style={{
                      display: 'flex',
                      gap: 'var(--space-2)',
                      marginTop: 'var(--space-2)',
                      flexWrap: 'wrap',
                    }}
                  >
                    <input
                      className="input"
                      style={{ flex: 1 }}
                      value={newListName}
                      autoFocus
                      maxLength={120}
                      placeholder="Name der Liste, z. B. Newsletter"
                      aria-label="Name der neuen Liste"
                      onChange={(e) => setNewListName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void handleCreateList();
                        }
                        if (e.key === 'Escape') setListFormOpen(false);
                      }}
                    />
                    <Button
                      onClick={() => void handleCreateList()}
                      disabled={newListName.trim().length === 0}
                      loading={createList.isPending}
                    >
                      Anlegen
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setListFormOpen(false);
                        setNewListName('');
                      }}
                    >
                      Abbrechen
                    </Button>
                  </div>
                )}

                {createList.isError && (
                  <div className="alert" data-tone="danger" role="alert">
                    {createList.error instanceof Error
                      ? createList.error.message
                      : 'Die Liste konnte nicht angelegt werden.'}
                  </div>
                )}
              </div>

              <div className="field">
                <label className="field__label" htmlFor="import-consent">
                  Einwilligung dokumentieren als
                </label>
                <select
                  id="import-consent"
                  className="input"
                  value={consentStatus}
                  onChange={(e) => setConsentStatus(e.target.value)}
                >
                  <option value="unknown">Unbekannt</option>
                  <option value="confirmed">Bestätigt (Double-Opt-In liegt vor)</option>
                  <option value="pending">Ausstehend</option>
                </select>
                <span className="field__hint">
                  Nur „Bestätigt" wählen, wenn für diese Adressen tatsächlich eine Einwilligung
                  vorliegt — der Eintrag gilt als Nachweis.
                </span>
              </div>

              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={updateExisting}
                  onChange={(e) => setUpdateExisting(e.target.checked)}
                />
                <span>
                  Bestehende Kontakte aktualisieren
                  <span className="field__hint" style={{ display: 'block' }}>
                    Leere Spalten überschreiben keine vorhandenen Werte.
                  </span>
                </span>
              </label>

              {runImport.isError && (
                <div className="alert" data-tone="danger" role="alert">
                  {runImport.error instanceof Error
                    ? runImport.error.message
                    : 'Der Import ist fehlgeschlagen.'}
                </div>
              )}

              <div className="dialog__actions">
                <Button variant="secondary" onClick={() => setStep('upload')}>
                  Zurück
                </Button>
                <Button
                  onClick={handleRun}
                  disabled={mappingProblem !== null}
                  loading={runImport.isPending}
                >
                  {upload.preview.totalRowsEstimate.toLocaleString('de-CH')} Zeilen importieren
                </Button>
              </div>
            </div>
          </section>
        </>
      )}

      {step === 'done' && result && <ImportSummary result={result} />}
    </div>
  );
}

function ImportSummary({ result }: { result: ImportResult }) {
  return (
    <section className="card">
      <div className="card__body stack">
        <h2 style={{ fontSize: 'var(--text-lg)' }}>Import abgeschlossen</h2>

        <dl className="definition-grid">
          <div>
            <dt>Zeilen gelesen</dt>
            <dd data-numeric>{result.totalRows.toLocaleString('de-CH')}</dd>
          </div>
          <div>
            <dt>Neu angelegt</dt>
            <dd data-numeric>{result.created.toLocaleString('de-CH')}</dd>
          </div>
          <div>
            <dt>Aktualisiert</dt>
            <dd data-numeric>{result.updated.toLocaleString('de-CH')}</dd>
          </div>
          <div>
            <dt>Übersprungen</dt>
            <dd data-numeric>{result.skipped.toLocaleString('de-CH')}</dd>
          </div>
        </dl>

        {result.issues.length > 0 && (
          <div className="stack" style={{ gap: 'var(--space-2)' }}>
            <h3 className="field__label">Übersprungene Zeilen</h3>
            <p className="muted text-sm">
              Jede übersprungene Zeile mit Begründung — nichts wurde stillschweigend verworfen.
            </p>
            <div className="table-wrap" style={{ maxHeight: '320px', overflowY: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Zeile</th>
                    <th scope="col">Adresse</th>
                    <th scope="col">Grund</th>
                  </tr>
                </thead>
                <tbody>
                  {result.issues.map((issue, index) => (
                    <tr key={`${issue.row}-${index}`}>
                      <td data-numeric>{issue.row}</td>
                      <td>{issue.email ?? '—'}</td>
                      <td className="text-sm">{issue.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="dialog__actions">
          <Link to="/kontakte" className="btn" data-variant="primary">
            Zu den Kontakten
          </Link>
        </div>
      </div>
    </section>
  );
}

function SourceTab({
  id,
  label,
  hint,
  active,
  onSelect,
}: {
  id: Source;
  label: string;
  hint: string;
  active: boolean;
  onSelect: (id: Source) => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      className="tabs__tab"
      aria-selected={active}
      onClick={() => onSelect(id)}
    >
      {label}
      <span className="muted text-xs">{hint}</span>
    </button>
  );
}

function StepMarker({
  index,
  label,
  active,
  done,
}: {
  index: number;
  label: string;
  active: boolean;
  done: boolean;
}) {
  return (
    <li className="steps__item" data-active={active} data-done={done}>
      <span className="steps__number">{done ? '✓' : index}</span>
      <span>{label}</span>
    </li>
  );
}
